import * as loglevel from 'loglevel'
import axios, { AxiosRequestConfig } from 'axios'
import {
  defaultAppName,
  LogFormattedData,
  LoglevelPluginServerOptions,
  QueuedMessage
} from './types'
import { isPlainObject, defaultJSONFormat, Deferred } from './utils'

const loglevelServerSend = (
  logger: loglevel.Logger,
  options: LoglevelPluginServerOptions
) => {
  if (!logger || !logger.methodFactory) {
    throw new Error(
      'loglevel instance has to be specified in order to be extended'
    )
  }

  let pluginIsSending = false
  const pluginLogger = logger
  const pluginUrl = options?.url || 'http://localhost:5015/log'
  const pluginCallOriginal = options?.callOriginal || false
  const pluginLevel = options?.level || loglevel.levels.TRACE
  const pluginFormat = options?.format || defaultJSONFormat
  const pluginReqHeaders = options?.reqHeaders || {
    'Content-Type': 'application/json'
  }
  const pluginOriginalFactory = pluginLogger.methodFactory
  const pluginSendQueue: QueuedMessage[] = []

  const sendNextMessage = (): Promise<void> => {
    if (!pluginSendQueue.length || pluginIsSending) {
      return Promise.resolve()
    }

    pluginIsSending = true

    const {
      deferred,
      formattedMessage,
      messageAppName = defaultAppName
    } = pluginSendQueue.shift() as QueuedMessage

    const config: AxiosRequestConfig = {
      headers: {
        ...pluginReqHeaders,
        'X-Origin': messageAppName
      }
    }

    return axios
      .post(pluginUrl, formattedMessage, config)
      .then(() => {
        pluginIsSending = false
        deferred.resolve()
        setTimeout(sendNextMessage, 0)
      })
      .catch(e => {
        pluginIsSending = false
        deferred.reject(e)
        setTimeout(sendNextMessage, 0)
      })
  }

  const customMethodFactory = (
    methodName: loglevel.LogLevelNames,
    logLevel: loglevel.LogLevelNumbers,
    loggerName: string | symbol
  ): ((...message: any[]) => Promise<void>) => {
    const rawMethod = pluginOriginalFactory(methodName, logLevel, loggerName)

    return (message: LogFormattedData | string): Promise<void> => {
      const formattedMessage: string = pluginFormat({
        message,
        methodName,
        logLevel,
        loggerName: String(loggerName)
      })

      let messageAppName: string | undefined

      if (pluginCallOriginal) {
        rawMethod(message)
      }

      if (typeof message !== 'string' && isPlainObject(message)) {
        messageAppName = message.appName
      }

      const DeferredSendLogMessage = new Deferred<void>()

      pluginSendQueue.push({
        formattedMessage,
        messageAppName,
        deferred: DeferredSendLogMessage
      })

      sendNextMessage()

      return DeferredSendLogMessage.promise
    }
  }

  // these two lines overwrite default methodFactory used for creating all different log type methods (error, debug, etc.)
  logger.methodFactory = customMethodFactory
  logger.setLevel(pluginLevel)
}

export default loglevelServerSend
